import { applyAction, legalActions, viewFor } from './engine';
import type { Action, Color, GameState } from './types';

export const WIN = 1_000_000;

function evaluate(s: GameState, me: Color): number {
  if (s.result) {
    if (!s.result.winner) return 0;
    return s.result.winner === me ? WIN - s.ply : -WIN + s.ply;
  }
  let score = 0;
  const cx = (s.w - 1) / 2;
  const cy = (s.h - 1) / 2;
  for (let sq = 0; sq < s.board.length; sq++) {
    const p = s.board[sq];
    if (!p) continue;
    const def = s.rs.pieces[p.type];
    let v = (def?.royal ? 0 : def?.value ?? 1) * 100;
    if (!def?.royal) {
      const d = Math.abs((sq % s.w) - cx) + Math.abs(Math.floor(sq / s.w) - cy);
      v += Math.max(0, 8 - d) * 2;
      if (def?.promotes) v += (p.color === 'w' ? Math.floor(sq / s.w) : s.h - 1 - Math.floor(sq / s.w)) * 4;
    }
    if (p.fuse) v -= 20;
    // A ready Multiply is almost a piece already: using it now or later should not swing the score.
    if (def && p.parentId == null && !p.cd.multiply) {
      const ab = def.abilities.find((a) => a.kind === 'multiply' && a.enabled);
      if (ab && s.board.filter((q) => q?.parentId === p.id).length < (ab.max ?? 2)) v += def.value * 85;
    }
    score += p.color === me ? v : -v;
  }
  return score;
}

function order(s: GameState, acts: Action[]): Action[] {
  const val = (a: Action) => {
    const tgt = a.t === 'move' ? a.to : a.target ?? -1;
    const victim = tgt >= 0 ? s.board[tgt] : null;
    let v = victim && victim.color !== s.turn ? (s.rs.pieces[victim.type]?.value ?? 1) * 10 + 5 : 0;
    if (a.t === 'move' && a.promo) v += 50;
    if (a.t === 'ability' && (a.kind === 'detonate' || a.kind === 'fireBreath')) v += 3;
    return v;
  };
  return acts.map((a) => [val(a), a] as const).sort((x, y) => y[0] - x[0]).map((x) => x[1]);
}

/** Captures and promotions: the moves that are still played out after the search depth runs out. */
export function isNoisy(s: GameState, a: Action): boolean {
  if (a.t !== 'move') return false;
  if (a.promo || a.epCapture != null) return true;
  const victim = s.board[a.to];
  return !!victim && victim.color !== s.turn;
}

interface Ctx { me: Color; deadline: number; nodes: number; aborted: boolean; q: number }

const tick = (ctx: Ctx) => {
  if ((++ctx.nodes & 255) === 0 && Date.now() > ctx.deadline) ctx.aborted = true;
  return ctx.aborted;
};

/**
 * Play out captures until the position is quiet, so a piece left hanging anywhere on the board
 * is noticed even at the end of the search. Either side may stop capturing ("stand pat").
 */
function quiesce(s: GameState, alpha: number, beta: number, ctx: Ctx, qd: number, pv: Action[]): number {
  pv.length = 0;
  if (tick(ctx)) return 0;
  const stand = evaluate(s, ctx.me);
  if (s.result || qd <= 0) return stand;
  const maximizing = s.turn === ctx.me;
  if (maximizing ? stand >= beta : stand <= alpha) return stand;
  if (maximizing) alpha = Math.max(alpha, stand); else beta = Math.min(beta, stand);
  let best = stand;
  const line: Action[] = [];
  for (const a of order(s, legalActions(s).filter((x) => isNoisy(s, x)))) {
    const v = quiesce(applyAction(s, a).state, alpha, beta, ctx, qd - 1, line);
    if (maximizing ? v > best : v < best) { best = v; pv.length = 0; pv.push(a, ...line); }
    if (maximizing) alpha = Math.max(alpha, v); else beta = Math.min(beta, v);
    if (beta <= alpha) break;
  }
  return best;
}

/** Alpha-beta search; `pv` receives the expected continuation from `s`. */
function search(s: GameState, depth: number, alpha: number, beta: number, ctx: Ctx, pv: Action[]): number {
  pv.length = 0;
  if (tick(ctx)) return 0;
  if (s.result) return evaluate(s, ctx.me);
  if (depth <= 0) return ctx.q ? quiesce(s, alpha, beta, ctx, ctx.q, pv) : evaluate(s, ctx.me);
  const acts = order(s, legalActions(s));
  if (!acts.length) return evaluate(s, ctx.me);
  const maximizing = s.turn === ctx.me;
  let best = maximizing ? -Infinity : Infinity;
  const line: Action[] = [];
  for (const a of acts) {
    const v = search(applyAction(s, a).state, depth - 1, alpha, beta, ctx, line);
    if (maximizing ? v > best : v < best) { best = v; pv.length = 0; pv.push(a, ...line); }
    if (maximizing) alpha = Math.max(alpha, v); else beta = Math.min(beta, v);
    if (beta <= alpha) break;
  }
  return best;
}

/**
 * Strength ladder: deeper search, more time and less random noise per level.
 * `q` is how many captures are still played out after the search depth.
 */
export const BOT_LEVELS = [
  { level: 1, name: 'Beginner', depth: 1, noise: 700, timeMs: 250, q: 0 },
  { level: 2, name: 'Rookie', depth: 1, noise: 350, timeMs: 300, q: 0 },
  { level: 3, name: 'Casual', depth: 1, noise: 120, timeMs: 500, q: 0 },
  { level: 4, name: 'Club', depth: 2, noise: 40, timeMs: 1000, q: 2 },
  { level: 5, name: 'Strong', depth: 2, noise: 12, timeMs: 1500, q: 4 },
  { level: 6, name: 'Expert', depth: 3, noise: 5, timeMs: 2500, q: 4 },
  { level: 7, name: 'Master', depth: 3, noise: 0, timeMs: 4000, q: 6 },
  { level: 8, name: 'Grandmaster', depth: 4, noise: 0, timeMs: 6000, q: 6 },
];

export interface BotThought {
  action: Action;
  score: number;
  /** Expected continuation after `action` (opponent's reply first). */
  line?: Action[];
}
export interface BotAnalysis {
  action: Action | null;
  /** Best root moves of the deepest finished search, best first. */
  candidates: BotThought[];
  depth: number;
}

/**
 * Score every root move of `s` for the side to move (iterative deepening up to `maxDepth`,
 * stopping at the deadline). Scores of the deepest finished iteration, best first.
 */
export function scoreRootMoves(s: GameState, maxDepth: number, timeMs: number, noise = 0, rand: () => number = Math.random, q = 0): { scores: BotThought[]; depth: number } {
  const root = order(s, legalActions(s));
  const me = s.turn;
  const deadline = Date.now() + timeMs;
  let scores: BotThought[] = [];
  let finished = 0;
  // Shrink the depth on large boards so each iteration finishes.
  const limit = root.length > 60 ? Math.min(maxDepth, 2) : maxDepth;
  const line: Action[] = [];
  for (let depth = 1; depth <= limit && root.length; depth++) {
    const ctx: Ctx = { me, deadline, nodes: 0, aborted: false, q };
    const these: BotThought[] = [];
    for (const a of root) {
      const v = search(applyAction(s, a).state, depth - 1, -Infinity, Infinity, ctx, line) + (noise ? (rand() - 0.5) * noise : 0);
      if (ctx.aborted) break;
      these.push({ action: a, score: v, line: line.slice() });
    }
    if (ctx.aborted && depth > 1) break;
    scores = these;
    finished = depth;
  }
  return { scores: scores.sort((a, b) => b.score - a.score), depth: finished };
}

/** Search the position for the side to move. The bot only sees what that side may see. */
export function analyzeBot(full: GameState, level: number, rand: () => number = Math.random): BotAnalysis {
  const cfg = BOT_LEVELS[Math.min(BOT_LEVELS.length, Math.max(1, level)) - 1];
  const s = viewFor(full, full.turn);
  const { scores, depth } = scoreRootMoves(s, cfg.depth, cfg.timeMs, cfg.noise, rand, cfg.q);
  if (!scores.length) return { action: null, candidates: [], depth: 0 };
  return { action: scores[0].action, candidates: scores.slice(0, 5), depth };
}

/** Pick a move for the side to move. */
export function chooseBotAction(full: GameState, level: number, rand: () => number = Math.random): Action | null {
  return analyzeBot(full, level, rand).action;
}
